/**
 * Shared people picker (kudos, new message, channels, members, acknowledgements, events, tasks, groups).
 * Selected people sit at the top as removable chips with a count; below is a search box. The list shows search
 * results only (t683u: no suggested people before you type), with selected people pinned first. Keyboard: arrows move,
 * Enter adds the highlighted person, Backspace in an empty box removes the last chip. `single` replaces instead of
 * adding. Helper text is the caller's: there is none by default, so copy written for one context can't leak into another.
 */
import { useId, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Check, Search, X } from 'lucide-react';
import type { WorkspaceMember } from '@tradeoutlet/shared';
import { useMembers } from '@/lib/hooks';
import { Avatar } from '@/components/ui';

export interface PeoplePickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
  exclude?: string[];
  /** One person only: picking someone replaces the current choice. */
  single?: boolean;
  /** Most people that can be picked (e.g. the group chat cap). */
  max?: number;
  /** Context-specific helper text shown under the picker. None by default. */
  helper?: ReactNode;
  /** People to suggest before anything is typed (e.g. recent contacts). Defaults to the first few in the workspace. */
  suggested?: string[];
  placeholder?: string;
  /** Offer only these people (e.g. a conversation's members). */
  only?: string[];
  /** Accessible name for the search box. */
  label?: string;
  /** t226u: no suggestions before anything is typed (and no Show everyone): only search results. */
  searchOnly?: boolean;
  /** t351u: people you follow, listed first under Following (search still filters them). */
  following?: string[];
  /** t557u: past this many chosen, the chips give way to a muted count ("42 people"); they stay first in the list below to untick. */
  chipsUpTo?: number;
}

export function PeoplePicker({ selected, onChange, exclude = [], single, max, helper, placeholder, label = 'Search people', only, following, chipsUpTo }: PeoplePickerProps) {
  const { members, byId } = useMembers();
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();
  const pool = useMemo(() => members.filter(m => m.status === 'active' && !exclude.includes(m.user_id) && (!only || only.includes(m.user_id))), [members, exclude, only]);
  const chosen = selected.map(id => byId.get(id)).filter((m): m is WorkspaceMember => Boolean(m));
  const full = max !== undefined && !single && selected.length >= max;
  const term = q.trim().toLowerCase();
  const rows = useMemo(() => {
    const hit = (m: WorkspaceMember) => !term || m.display_name.toLowerCase().includes(term) || (m.email ?? '').toLowerCase().includes(term) || (m.job_title ?? '').toLowerCase().includes(term);
    const picked = pool.filter(m => selected.includes(m.user_id) && hit(m));
    const unpicked = pool.filter(m => !selected.includes(m.user_id) && hit(m));
    const fset = new Set(following ?? []);
    const fol = unpicked.filter(m => fset.has(m.user_id));
    const rest = unpicked.filter(m => !fset.has(m.user_id));
    if (term) return { picked, fol, rest, more: 0 };
    // t683u: no suggested people (or Following/Show everyone list) before anything is typed: the chosen stay listed to
    // untick and typing finds anyone. A short fixed list (`only`, e.g. a conversation's members) is the choice itself, so
    // it is listed in full. `suggested` and `searchOnly` are kept for callers but no longer change anything.
    if (only) return { picked, fol, rest, more: 0 };
    return { picked, fol: [], rest: [], more: 0 };
  }, [pool, selected, term, following, only]);
  const flat = [...rows.picked, ...rows.fol, ...rows.rest];
  const active = flat[Math.min(hi, flat.length - 1)];

  const toggle = (id: string) => {
    const on = selected.includes(id);
    if (single) onChange(on ? [] : [id]);
    else if (on) onChange(selected.filter(x => x !== id));
    else if (!full) onChange([...selected, id]);
    setQ(''); setHi(0); input.current?.focus();
  };
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (active) toggle(active.user_id); }
    else if (e.key === 'Backspace' && !q && selected.length) onChange(selected.slice(0, -1));
  };

  const row = (m: WorkspaceMember, i: number) => {
    const on = selected.includes(m.user_id);
    return (
      <li key={m.user_id} id={`${listId}-${m.user_id}`} role="option" aria-selected={on} data-testid="people-picker-option"
        onMouseDown={e => e.preventDefault()} onClick={() => toggle(m.user_id)} onMouseEnter={() => setHi(i)}
        className={clsx('flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5', i === hi ? 'bg-slate-100 dark:bg-slate-800' : '', !on && full && 'opacity-50')}>
        <Avatar size={26} name={m.display_name} id={m.user_id} />
        <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{m.display_name}</span><span className="block truncate text-xs text-slate-500">{m.job_title ?? m.email}</span></span>
        {on && <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300"><Check className="h-4 w-4" />Selected</span>}
      </li>
    );
  };

  return (
    <div data-testid="people-picker">
      {chipsUpTo !== undefined && !single && chosen.length > chipsUpTo ? (
        <p className="mb-2 text-xs text-slate-500" data-testid="people-picker-count">{chosen.length} people{max !== undefined ? ` of up to ${max}` : ''}</p>
      ) : chosen.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="people-picker-chips">
          {!single && <span className="mr-1 text-xs font-medium text-slate-600 dark:text-slate-300" data-testid="people-picker-count">{chosen.length} selected{max !== undefined ? ` of up to ${max}` : ''}</span>}
          {chosen.map(m => (
            <span key={m.user_id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white py-0.5 pl-0.5 pr-1 text-sm dark:border-slate-600 dark:bg-slate-800" data-testid="people-picker-chip">
              <Avatar size={22} name={m.display_name} id={m.user_id} />{m.display_name}
              <button type="button" aria-label={`Remove ${m.display_name}`} onClick={() => toggle(m.user_id)} className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:text-white"><X className="h-3.5 w-3.5" /></button>
            </span>
          ))}
        </div>
      )}
      <label className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:focus-within:ring-white">
        <Search className="h-4 w-4 text-slate-500" aria-hidden />
        <input ref={input} value={q} onChange={e => { setQ(e.target.value); setHi(0); }} onKeyDown={onKey} data-testid="people-picker-input"
          role="combobox" aria-expanded aria-controls={listId} aria-autocomplete="list" aria-label={label} aria-activedescendant={active ? `${listId}-${active.user_id}` : undefined}
          placeholder={placeholder ?? (single ? 'Search for a person' : chosen.length ? 'Add someone else' : 'Search people')} className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400" />
      </label>
      {full && <p className="mt-1 text-xs text-slate-600 dark:text-slate-300" role="status">That’s the most you can add here ({max}).</p>}
      <ul id={listId} role="listbox" aria-multiselectable={!single} aria-label="People" className="scroll-thin mt-2 max-h-56 space-y-0.5 overflow-y-auto">
        {rows.picked.length > 0 && <li role="presentation" className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Selected</li>}
        {rows.picked.map((m, i) => row(m, i))}
        {rows.fol.length > 0 && <li role="presentation" className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500" data-testid="people-picker-following">Following</li>}
        {rows.fol.map((m, i) => row(m, rows.picked.length + i))}
        {rows.rest.length > 0 && <li role="presentation" className="px-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">{rows.fol.length ? 'Other results' : 'Results'}</li>}
        {rows.rest.map((m, i) => row(m, rows.picked.length + rows.fol.length + i))}
        {term && !flat.length && <li role="presentation" className="px-2 py-2 text-sm text-slate-500">No one matches “{q.trim()}”.</li>}
      </ul>
      {helper && <p className="mt-2 text-xs text-slate-600 dark:text-slate-300" data-testid="people-picker-helper">{helper}</p>}
    </div>
  );
}
